import { FormikValues } from 'formik';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalSearchList } from '../../../context/GlobalSearchContext';
import { useAlert } from '../../../hooks/useAlert';
import { LegalType } from '../../../model/common';
import { GlobalSearchTableRow } from '../../../model/globalSearch';
import SysConfigStore from '../../../store/common/SysConfigStore';
import { getLegalLinkMap, openToAdele } from '../../../util/commonUtils';
import ButtonFront from '../../ui/buttons/ButtonFront';
import ButtonText from '../../ui/buttons/ButtonText';
import FileChip from '../../ui/chips/FileChip';
import LabelChip from '../../ui/chips/LabelChip';
import Highlight from '../../ui/highlights/Highlight';
import LawsuitIcon from '../../ui/icons/apps/custom/sk/LawsuitIcon';
import TableFooter from '../../ui/tables/TableFooter';
import ItemSearchField from '../ItemSearchField';
import PopupPreviewFile from '../PopupPreviewFile';
import PopupPreviewSmail from '../PopupPreviewSmail';

interface IProps {
	data: GlobalSearchTableRow;
	formik: FormikValues;
}

export default function LawsuitPanel({ data, formik }: IProps) {
	const { t } = useTranslation();
	const { labelText } = SysConfigStore();
	const { searchParams, setSearchParams } = useGlobalSearchList();
	const [snackbar] = useAlert();
	const handleNavigate = async (id: number, type: LegalType) => {
		const { success, url, msg } = await getLegalLinkMap(type, id);
		if (success) {
			openToAdele(url);
		} else {
			snackbar(msg, 'error');
		}
	};
	const handleChangePage = (e: unknown, newPage: number) => {
		setSearchParams(prev => ({ ...prev, currentPage: newPage }));
	};

	const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchParams(prev => ({ ...prev, rowsPerPage: parseInt(e.target.value, 10), currentPage: 0 }));
	};
	return (
		<>
			<p className='font-bold'>
				{labelText('dispute')} {data.lawsuitCnt}
			</p>
			<ul className='col-span-9 grid gap-12 [&>li]:pb-12 [&>li:last-of-type]:pb-0 [&>li]:border-b [&>li:last-child]:border-none'>
				{data &&
					data.lawsuitList.map(row => (
						<li key={row.id} className='grid gap-6'>
							{formik.values.isTitle && (
								<section>
									<div className='flex items-center gap-2'>
										<LawsuitIcon className='text-neutral-500' />
										<ButtonText
											className='text-lg font-bold'
											onClick={() => handleNavigate(row.id, 'LAWSUIT')}
										>
											<Highlight text={row.title} pattern='' />
										</ButtonText>
									</div>
								</section>
							)}
							<section className='grid grid-cols-4 gap-6'>
								{formik.values.isDescription && (
									<>
										<ItemSearchField title={t('dispute_label_management_company')}>
											<p>
												<Highlight text={row.company} pattern='' />
											</p>
										</ItemSearchField>
										<ItemSearchField title={t('label_legal_type')}>
											<div className='flex flex-wrap items-center gap-1'>
												{row.label_names !== ''
													? row.label_names.split('|').map((item, idx) => (
															/* eslint-disable-next-line react/no-array-index-key */
															<li key={idx} className='flex gap-2'>
																<LabelChip
																	id='label-1'
																	disabled
																	tag={{
																		id: 1,
																		name: <Highlight text={item} pattern='' />,
																		value: item,
																		color: row.label_colors.split('|')[idx],
																		type: 'public',
																		extended_type: 'DEFAULT',
																		creator_id: 1,
																	}}
																/>
															</li>
													  ))
													: ''}
											</div>
										</ItemSearchField>
										<ItemSearchField title={t('label_division')}>
											<p>
												<Highlight
													text={
														row.dispute_type === 'LAWSUIT'
															? t('dispute_label_lawsuit_etc')
															: t('dispute_label_investigation')
													}
													pattern=''
												/>
											</p>
										</ItemSearchField>
										<ItemSearchField title={t('label_external_lawyer')}>
											<p>
												{row.external_lawyer_name !== ''
													? row.external_lawyer_name.split('|').map((item, idx) => (
															/* eslint-disable-next-line react/no-array-index-key */
															<li key={idx} className='flex gap-2'>
																<Highlight
																	text={`${item} / ${
																		row.external_lawyer_email.split('|')[idx]
																	}`}
																	pattern=''
																/>
															</li>
													  ))
													: ''}
											</p>
										</ItemSearchField>
										<ItemSearchField title={t('dispute_label_jurisdiction')}>
											<p>
												<Highlight text={row.compotent_court} pattern='' />
											</p>
										</ItemSearchField>
										<ItemSearchField title={t('MSG_ID_217')}>
											<p>
												<Highlight text={row.case_number} pattern='' />
											</p>
										</ItemSearchField>
										<ItemSearchField title={t('MSG_ID_218')}>
											<p>
												<Highlight text={row.parties_name} pattern='' />
											</p>
										</ItemSearchField>
										<ItemSearchField title={t('label_situation')}>
											<p>
												<Highlight text={row.suit_progress} pattern='' />
											</p>
										</ItemSearchField>
										<ItemSearchField
											className='col-span-4 items-start'
											title={t('dispute_label_facts')}
											value={row.explain}
										>
											<div className='flex-1'>
												<Highlight text={row.explain} pattern='' />
											</div>
										</ItemSearchField>
										<ItemSearchField
											className='col-span-4 items-start'
											title={t('dispute_label_main_issues')}
											value={row.main_issue_contents}
										>
											<div className='flex-1'>
												<Highlight text={row.main_issue_contents} pattern='' />
											</div>
										</ItemSearchField>
									</>
								)}

								{formik.values.isFile &&
									data.attachList
										.filter(item => item.doc_type === 'bsuitfile')
										.filter(item => item.lawsuit_id === row.id).length > 0 && (
										<ItemSearchField
											className='col-span-4 items-center'
											title={t('label_document_management')}
										>
											<ul className='flex-1 flex flex-wrap items-center gap-2'>
												{data.attachList
													.filter(item => item.doc_type === 'bsuitfile')
													.filter(item => item.lawsuit_id === row.id)
													.map(item => (
														<li key={item.pk} className='flex gap-2'>
															<FileChip
																text={<Highlight text={item.file_name} pattern='' />}
																disabled
															/>
															<PopupPreviewFile
																data={
																	data.attachPreviewList.find(
																		attach => attach.pk === item.pk,
																	) ?? { pk: '', file_contents_summary: '' }
																}
																allText={item}
															/>
														</li>
													))}
											</ul>
										</ItemSearchField>
									)}

								{formik.values.isFile &&
									data.attachList
										.filter(item => item.doc_type === 'bdecisionfile')
										.filter(item => item.lawsuit_id === row.id).length > 0 && (
										<ItemSearchField className='col-span-4 items-center' title={t('MSG_ID_211')}>
											<ul className='flex-1 flex flex-wrap items-center gap-2'>
												{data.attachList
													.filter(item => item.doc_type === 'bdecisionfile')
													.filter(item => item.lawsuit_id === row.id)
													.map(item => (
														<li key={item.pk} className='flex gap-2'>
															<FileChip
																text={<Highlight text={item.file_name} pattern='' />}
																disabled
															/>
															<PopupPreviewFile
																data={
																	data.attachPreviewList.find(
																		attach => attach.pk === item.pk,
																	) ?? { pk: '', file_contents_summary: '' }
																}
																allText={item}
															/>
														</li>
													))}
											</ul>
										</ItemSearchField>
									)}

								{formik.values.isDescription &&
									data.commentList
										.filter(item => item.comment_type === 'law_smail')
										.filter(item => item.lawsuit_id === row.id).length > 0 && (
										<ItemSearchField
											className='col-span-4 items-start flex flex-col'
											title={t('cmmn_label_smart_email')}
										>
											{data.commentList
												.filter(item => item.comment_type === 'law_smail')
												.filter(item => item.lawsuit_id === row.id)
												.map(item => (
													<div key={item.pk} className='flex-wrap gap-2'>
														<Highlight
															text={JSON.parse(item.comment).message_txt}
															pattern=''
														/>
														<PopupPreviewSmail data={item} />
													</div>
												))}
										</ItemSearchField>
									)}

								{formik.values.isFile &&
									data.attachList
										.filter(item => item.doc_type === 'law_smail_file')
										.filter(item => item.lawsuit_id === row.id).length > 0 && (
										<ItemSearchField
											className='col-span-4 items-center'
											title={t('cmmn_label_smart_email_file')}
										>
											<ul className='flex-1 flex flex-wrap items-center gap-2'>
												{data.attachList
													.filter(item => item.doc_type === 'law_smail_file')
													.filter(item => item.lawsuit_id === row.id)
													.map(item => (
														<li key={item.pk} className='flex gap-2'>
															<FileChip
																text={<Highlight text={item.file_name} pattern='' />}
																disabled
															/>
															<PopupPreviewFile
																data={
																	data.attachPreviewList.find(
																		attach => attach.pk === item.pk,
																	) ?? { pk: '', file_contents_summary: '' }
																}
																allText={item}
															/>
														</li>
													))}
											</ul>
										</ItemSearchField>
									)}
							</section>
						</li>
					))}
			</ul>
			{searchParams.searchCtg === 'ALL' ? (
				data.lawsuitCnt !== 0 && (
					<div className='col-span-10 flex justify-end pt-12'>
						<ButtonFront
							text={t('global_search_see_all')}
							onClick={() =>
								setSearchParams(prev => ({
									...prev,
									searchCtg: 'LAWSUIT',
								}))
							}
						/>
					</div>
				)
			) : (
				<TableFooter
					className='col-span-10 flex justify-end pt-12'
					rowsPerPageOptions={[10, 20, 30, 50]}
					showFirstButton
					showLastButton
					count={data.lawsuitCnt}
					rowsPerPage={searchParams.rowsPerPage}
					page={searchParams.currentPage}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			)}
		</>
	);
}
